import React from 'react';
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import ActivityIndicator from '@components/ActivityIndicator';
import Icon from '@components/Icon';
import { TextInput, TextInputIcon, TextInputRef } from '@components/TextInput';
import { useAppTheme } from '@context/ThemeContext';

const DEFAULT_SEND_ICON = 'send-circle';
const DEFAULT_SEND_ICON_SIZE = 35;

type ComposerIconName = React.ComponentProps<typeof Icon>['name'];

export type ComposerProps = {
  /** Current draft text shown in the composer input. */
  value: string;
  /** Called whenever the user edits the draft text. */
  onChangeText: (text: string) => void;
  /** Called when the send affordance is pressed. */
  onSend: () => void;
  /** Disables the send affordance while preserving the editable input. */
  disabled?: boolean;
  /** Shows a loading indicator in place of the send icon. */
  loading?: boolean;
  /** Optional input placeholder text. */
  placeholder?: string;
  /** MaterialCommunityIcons name used for the send affordance. */
  sendIcon?: ComposerIconName;
  /** Size of the send icon. */
  sendIconSize?: number;
  /** Accessibility label for the send affordance. */
  sendAccessibilityLabel?: string;
  /** Optional style for the outer composer container. */
  style?: StyleProp<ViewStyle>;
  /** Optional style for the Paper text input. */
  inputStyle?: StyleProp<TextStyle>;
  /** Test id for the outer composer container. */
  testID?: string;
  /** Test id for the Paper text input. */
  inputTestID?: string;
  /** Test id for the send affordance. */
  sendTestID?: string;
};

export default function Composer({
  value,
  onChangeText,
  onSend,
  disabled = false,
  loading = false,
  placeholder,
  sendIcon = DEFAULT_SEND_ICON,
  sendIconSize = DEFAULT_SEND_ICON_SIZE,
  sendAccessibilityLabel = 'Send',
  style,
  inputStyle,
  testID,
  inputTestID,
  sendTestID,
}: ComposerProps) {
  const appTheme = useAppTheme();
  const inputRef = React.useRef<TextInputRef>(null);
  const sendDisabled = disabled || loading;

  const onPressSend = React.useCallback(() => {
    if (sendDisabled) {
      return;
    }

    onSend();
    inputRef.current?.focus();
  }, [onSend, sendDisabled]);

  return (
    <View style={[styles.wrapper, style]} testID={testID}>
      <TextInput
        ref={inputRef}
        cursorColor={appTheme.colors.primary}
        dense
        editable={!loading}
        keyboardAppearance={appTheme.dark ? 'dark' : 'light'}
        multiline
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={appTheme.colors.placeholder}
        returnKeyType="default"
        right={
          <TextInputIcon
            accessibilityLabel={sendAccessibilityLabel}
            color={sendDisabled ? appTheme.colors.disabled : appTheme.colors.primary}
            disabled={sendDisabled}
            forceTextInputFocus={false}
            icon={({ color, size }) =>
              loading ? (
                <ActivityIndicator color={color} size="small" />
              ) : (
                <Icon color={color} name={sendIcon} size={sendIconSize || size} />
              )
            }
            onPress={onPressSend}
            size={sendIconSize}
            testID={sendTestID}
          />
        }
        selectionColor={appTheme.colors.primary}
        contentStyle={styles.inputContent}
        style={[styles.input, inputStyle]}
        testID={inputTestID}
        textColor={appTheme.colors.text}
        value={value}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    width: '100%',
  },
  inputContent: {
    textAlignVertical: 'center',
  },
  wrapper: {
    alignSelf: 'stretch',
    flexShrink: 0,
    margin: 5,
  },
});
